/**
 * @author: YuKi
 * @description: 问题类型维护
 */
import React, { useCallback, memo, useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Row, Col, Space, message, Select, Checkbox } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getQuestionTypePage,
  editQuestionType,
  delQuestionType,
  refreshData,
  getQuestionTypeI18nList,
} from '@/api/clientFeedback/archivalMaintenance';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { LANGUAGE_MAP } from '@/constants/languageMap';
import { RootStateOrAny, useSelector } from 'react-redux';

/* 列表字段类型定义 */
type TableColumnsItem = {
  /** 行ID */
  id: string;
  /** 部件名称 */
  questionType: string;
  /** 创建人 */
  createAccount: string;
  /** 创建时间 */
  createDatetime: string;
  /** 产品线 */
  businessLine: number;
};

/* 列表查询参数 */
let searchParams = {
  /** 每页显示的条数 */
  pageSize: 10,
  /** 当前页 */
  pageIndex: 1,
  /** 问题类型 */
  questionType: '',
  /** 所属产品线 */
  businessLine: 10,
};

const ArchivalMaintenance: React.FC = () => {
  const { t } = useTranslation();
  const { confirm } = Modal;
  const history = useHistory();
  const location = useLocation();
  const [pagingRecord, setPagingRecord] = useState({ pageSize: 10, pageIndex: 1, total: 0 });
  const [dataList, setDataList] = useState<TableColumnsItem[]>([]);
  const [tableLoading, setTableLoading] = useState<any>(false);
  const [addOrEdit, setAddOrEdit] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectForm] = Form.useForm();
  const [searchForm] = Form.useForm();

  /* 获取语言环境 */
  const language = useSelector((state: RootStateOrAny) => state.get('app').get('language'));

  const [multiLanguageTextList, setMultiLanguageTextList] = useState<{ lgCode: string, text: string }[]>([]);

  /* 列表字段 */
  const columns: ColumnsType<TableColumnsItem> = [
    /* 序号 */
    {
      title: t('common.serialNumber'),
      width: 140,
      align: 'center',
      render: (text: any, record: any, index: any) => {
        return index + (pagingRecord.pageIndex - 1) * pagingRecord.pageSize + 1;
      },
    },
    /* 问题类型 */
    {
      title: t('createComplaint.issueTypes'),
      dataIndex: 'questionType',
      width: 180,
      align: 'center',
      ellipsis: true,
    },

    /* 创建人 */
    {
      title: t('common.creator'),
      dataIndex: 'createAccount',
      width: 160,
      align: 'center',
      ellipsis: true,
    },
    /* 创建时间 */
    {
      title: t('common.createTime'),
      dataIndex: 'createServerDatetime',
      width: 160,
      align: 'center',
      ellipsis: true,
    },
    /* 操作 */
    {
      title: t('common.operation'),
      align: 'center',
      width: 350,
      render: (text: any, record: any, index: any) => {
        return (
          <Space size="middle" wrap className="sn-management-space">
            {/* 编辑 */}
            <a onClick={() => displayAddOrEditDialog(2, record)}>{t('common.editBtn')}</a>
            {/* 部件维护 */}
            <a
              onClick={() => {
                jumpToModuleMaintenance(record);
              }}
            >
              {t('archivalMaintenance.problemMaintenance')}
            </a>
            {/* 刷新 */}
            <a
              onClick={() => {
                refresh(record.id);
              }}
            >
              {t('complaintList.refresh')}
            </a>
            {/* 删除 */}
            <a
              className="delete-text"
              onClick={() => {
                handleDelete(record.id);
              }}
            >
              {t('common.deleteBtn')}
            </a>
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    // 如果是从别的页面跳转过来,并且传递了参数,就先解析传递的参数,然后查询分页列表
    if (location.state && location.state.businessLine) {
      searchForm.setFieldsValue(location.state);
      searchParams = { ...searchParams, pageIndex: location.state.pageIndex || 1, ...searchForm.getFieldsValue() };
    }
    getQuestionType();
    return () => {
      searchParams = {
        pageSize: 10,
        pageIndex: 1,
        questionType: '',
        businessLine: 10,
      };
    };
  }, []);

  useEffect(() => {
    getQuestionType()
  }, [t])

  /**
   * @Author: YuKi
   * @description: 跳转至模块维护页面
   * @param {*} record
   */
  const jumpToModuleMaintenance = (record) => {
    if (history.location.pathname.indexOf('/innerStatic') < 0) {
      localStorage.setItem('pathname', history.location.pathname);
    }
    history.push({
      pathname: '/innerStatic/ComponentMaintenance/index',
      state: {
        questionTypeId: record.id,
        questionType: searchForm.getFieldValue('questionType') || '',
        businessLine: searchForm.getFieldValue('businessLine'),
        pageIndexQuestion: pagingRecord.pageIndex,
      },
    });
  };

  /**
   * @Author: YuKi
   * @description: 获取列表数据
   */
  const getQuestionType = () => {
    setTableLoading(true);
    getQuestionTypePage(searchParams)
      .then((rep: any) => {
        if (rep && rep.code === 200 && rep.data && rep.data.rows) {
          setDataList([...rep.data.rows]);
          let paging = {
            pageIndex: rep.data.pageIndex,
            pageSize: rep.data.pageSize,
            total: rep.data.total,
          };
          setPagingRecord({ ...paging });
        } else {
          setDataList([]);
          let pagingInit = {
            pageIndex: 1,
            pageSize: 10,
            total: 0,
          };
          setPagingRecord({ ...pagingInit });
        }
        setTableLoading(false);
      })
      .catch((err) => {
        setDataList([]);
        let pagingInit = {
          pageIndex: 1,
          pageSize: 10,
          total: 0,
        };
        setPagingRecord({ ...pagingInit });
        setTableLoading(false);
      });
  };

  const getQuestionTypeDetail = (id: string) => {
    getQuestionTypeI18nList({ id }).then((rep: any) => {
      if (rep && rep.code === 200 && rep.data) {
        setMultiLanguageTextList([...rep.data]);
        projectForm.setFieldsValue({
          languageList: rep.data.map(item => item.lgCode),
          ...rep.data.reduce((acc: Record<string, string>, item: { lgCode: string; text: string }) => {
            acc[item.lgCode] = item.text;
            return acc;
          }, {}),
        });
      } else {
        setMultiLanguageTextList([]);
        projectForm.resetFields();
      }
    }).catch((err) => {
      setMultiLanguageTextList([]);
      projectForm.resetFields();
    });
  };

  /**
   * @Author: YuKi
   * @description: 提交新增或编辑的表单
   */
  const addOrEditComponents = () => {
    if (addOrEdit === 1) {
      projectForm.validateFields().then((values) => {
        const { businessLine } = values
        editQuestionType({ businessLine, i18nList: multiLanguageTextList }).then((res: any) => {
          if (res.code == 200) {
            message.success(t('complaintList.operateSuccess'));
            getQuestionType();
            handleCancel();
          }
        });
      });
    } else {
      projectForm.validateFields().then((values) => {
        const { businessLine, id } = values
        editQuestionType({ id, businessLine, i18nList: multiLanguageTextList }).then((res: any) => {
          if (res.code == 200) {
            message.success(t('complaintList.operateSuccess'));
            getQuestionType();
            handleCancel();
          }
        });
      });
    }
  };

  /**
   * @Author: Wayne Hu
   * @description: 分页回调
   * @param {*} useCallback
   */
  const pageChange = useCallback((currentPage, currentSize) => {
    searchParams = { ...searchParams, ...searchForm.getFieldsValue() };
    searchParams.pageIndex = currentPage === undefined ? searchParams.pageIndex : currentPage;
    searchParams.pageSize = currentSize === undefined ? searchParams.pageSize : currentSize;
    getQuestionType();
  }, []);

  /**
   * @Author: Wayne Hu
   * @description: 按钮查询分页列表
   */
  const search = () => {
    searchParams = { ...searchParams, ...searchForm.getFieldsValue() };
    searchParams.pageIndex = 1;
    searchParams.pageSize = 10;
    getQuestionType();
  };

  /**
   * @Author: Wayne Hu
   * @description: 删除指定数据
   * @param {*} id 行id
   */
  const handleDelete = (id) => {
    confirm({
      title: t('common.tip'),
      closable: true,
      icon: <ExclamationCircleOutlined />,
      content: t('common.deleteData'),
      okText: t('common.confirmBtn'),
      cancelText: t('common.cancelBtn'),
      onOk() {
        delQuestionType({ id: id }).then((rep: any) => {
          if (rep && rep.code === 200) {
            message.success(t('common.deleteSuccess'));
            getQuestionType();
          }
        });
      },
      onCancel() {
        message.info(t('common.operateCancel'));
      },
    });
  };

  /**
   * @Author: Wayne Hu
   * @description: 打开新增或编辑弹窗
   * @param {number} type 弹窗类型 1-新增 2-编辑
   * @param {any} record
   */
  const displayAddOrEditDialog = (type: number, record?: any) => {
    setIsModalOpen(true);
    if (type === 1) {
      setAddOrEdit(1);
      setMultiLanguageTextList([{ lgCode: LANGUAGE_MAP.find(it => it.simple === language)?.value ?? 'zh-CN', text: '' }]);
      projectForm && projectForm.resetFields();
      projectForm && projectForm.setFieldsValue({
        languageList: [LANGUAGE_MAP.find(it => it.simple === language)?.value]
      })
    } else {
      setAddOrEdit(2);
      getQuestionTypeDetail(record.id);
      projectForm && projectForm.setFieldsValue({
        businessLine: record.businessLine,
        id: record.id,
      });
    }
  };

  /**
   * @Author: Wayne Hu
   * @description: 关闭弹窗
   */
  const handleCancel = () => {
    if (projectForm) {
      projectForm.resetFields();
    }
    setMultiLanguageTextList([]);
    setIsModalOpen(false);
  };

  /**
   * @Author: Wayne Hu
   * @description: 同步历史数据(列表外部)
   */
  const refreshAll = () => {
    refreshData({}).then((rep: any) => {
      if (rep.code == 200) {
        message.success(t('complaintList.operateSuccess'));
      }
    });
  };

  /**
   * @Author: Wayne Hu
   * @description: 同步历史数据(列表内部)
   * @param {*} id
   */
  const refresh = (id) => {
    refreshData({ questionTypeId: id }).then((rep: any) => {
      if (rep.code == 200) {
        message.success(t('complaintList.operateSuccess'));
      }
    });
  };

  const handleLanguageListChange = (value: CheckboxValueType[]) => {
    const selectedCodes = value as string[];
    setMultiLanguageTextList(prev =>
      selectedCodes.map(lgCode => ({
        lgCode,
        text: prev.find(it => it.lgCode === lgCode)?.text ?? '',
      }))
    );
  };

  return (
    <>
      <div className="custom-contant project-management">
        <Form
          form={searchForm}
          autoComplete="off"
          style={{ width: '100%' }}
          onFinish={search}
          className="custom-search in-line-style"
          layout="vertical"
        >
          <Row gutter={12} align={'bottom'}>
            {/* 部件名称 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item label={t('createComplaint.issueTypes')} name="questionType">
                <Input allowClear />
              </Form.Item>
            </Col>
            {/* 产品线 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item label={t('archivalMaintenance.productLine')} name="businessLine" initialValue={10}>
                <Select
                  placeholder={`${t('common.pleaseChoose')}`}
                  options={[
                    { value: 10, label: t('archivalMaintenance.householdUse') },
                    { value: 20, label: t('createComplaint.consumerBusiness') },
                    { value: 30, label: t('archivalMaintenance.industryAndCommerce') },
                    { value: 40, label: t('archivalMaintenance.largeEnergyStorage') },
                  ]}
                />
              </Form.Item>
            </Col>
            {/* 操作按钮 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item>
                <Space size="middle">
                  {/* 查询 */}
                  <Button type="primary" key="search" shape="round" htmlType="submit">
                    {t('common.searchBtn')}
                  </Button>
                  {/* 新增 */}
                  <Button type="primary" key="add" shape="round" onClick={() => displayAddOrEditDialog(1)}>
                    {t('common.addBtn')}
                  </Button>
                  {/* 同步 */}
                  <Button type="primary" key="sync" shape="round" onClick={() => refreshAll()}>
                    {t('complaintList.refresh')}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          columns={columns}
          loading={tableLoading}
          rowKey={(record) => record.id}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: false,
            defaultCurrent: 1,
            current: pagingRecord.pageIndex,
            total: pagingRecord.total,
            pageSize: pagingRecord.pageSize,
            onChange: pageChange,
          }}
          dataSource={dataList}
          scroll={{ x: 600, y: window.innerHeight - 350 }}
          className="custom-table-flex"
        />
      </div>

      {/* 新增/编辑弹窗 */}
      <Modal
        maskClosable={false}
        title={addOrEdit === 1 ? t('common.addBtn') : t('common.editBtn')}
        open={isModalOpen}
        onCancel={handleCancel}
        width="40%"
        footer={[
          /* 取消 */
          <Button shape="round" onClick={handleCancel}>
            {t('common.cancelBtn')}
          </Button>,
          /* 保存 */
          <Button shape="round" type="primary" onClick={addOrEditComponents}>
            {t('common.saveBtn')}
          </Button>,
        ]}
        forceRender
      >
        <Form autoComplete="off" layout="vertical" form={projectForm}>
          <Row gutter={12}>
            {/* 产品线 */}
            <Col span={24}>
              <Form.Item
                label={t('archivalMaintenance.productLine')}
                name="businessLine"
                rules={[
                  {
                    required: true,
                    message: `${t('archivalMaintenance.productLine')}${t('archivalMaintenance.parmasRequired')}`,
                  },
                ]}
              >
                <Select
                  placeholder={`${t('common.pleaseChoose')}`}
                  allowClear
                  options={[
                    { value: 10, label: t('archivalMaintenance.householdUse') },
                    { value: 20, label: t('createComplaint.consumerBusiness') },
                    { value: 30, label: t('archivalMaintenance.industryAndCommerce') },
                    { value: 40, label: t('archivalMaintenance.largeEnergyStorage') },
                  ]}
                />
              </Form.Item>
            </Col>
            {/* 问题类型 */}
            <Col span={24}>
              <Form.Item label={t('createComplaint.issueTypes')} name="languageList" rules={[{ required: true }]}
                getValueFromEvent={(value) => {
                  handleLanguageListChange(value)
                  return value;
                }}
              >
                <Checkbox.Group options={LANGUAGE_MAP} />
              </Form.Item>
            </Col>
            {
              multiLanguageTextList.map(item => {
                return (
                  <Col span={24}>
                    <Form.Item
                      label={LANGUAGE_MAP.find(it => it.value === item.lgCode)?.label}
                      name={item.lgCode}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                        },
                        {
                          validator: (_, value) => {
                            if (value && value.trim() && value.trim().length > 64) {
                              return Promise.reject(t('common.maxlengLimit', { maxLength: 64 }));
                            } else {
                              return Promise.resolve();
                            }
                          },
                        },
                      ]}
                    >
                      <Input
                        allowClear
                        onBlur={(e) => {
                          const value = e.target.value;
                          setMultiLanguageTextList(prev =>
                            prev.map(it => (it.lgCode === item.lgCode ? { ...it, text: value } : it))
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                )
              })
            }
            {addOrEdit === 2 ? (
              <Col span={24}>
                <Form.Item label={'ID'} hidden name="id">
                  <Input />
                </Form.Item>
              </Col>
            ) : null}
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default memo(ArchivalMaintenance);
